/**
 * GEDCOM 5.5.1: importar y exportar.
 *
 * Esto es un EXTRA, no el formato bueno, y conviene tener claro por que:
 * GEDCOM guarda el apellido en un solo campo `SURN`, asi que al exportar los
 * dos apellidos se juntan y se pierde cual viene del padre y cual de la
 * madre. Esa informacion es justo la que interesa conservar, asi que el
 * formato canonico aqui es .gramps y GEDCOM solo sirve para hablar con
 * plataformas web.
 *
 * Al importar se intenta deshacer el estropicio: si el campo de apellidos
 * tiene exactamente dos palabras se reparten como primero (del padre) y
 * segundo (de la madre), que es la convencion espanola. Es una SUPOSICION, y
 * se cuenta cuantas veces se hizo para que quien importa lo sepa y pueda
 * revisarlo.
 */
import {
  arbolVacio,
  type Arbol,
  type Evento,
  type Familia,
  type Fecha,
  type Lugar,
  type Persona,
  type Sexo,
} from "../model.js";
import { nombrePrincipal } from "../model.js";

const MESES = [
  "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
  "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
];

// ---------------------------------------------------------------------------
// Exportacion
// ---------------------------------------------------------------------------

function fechaAGedcom(f: Fecha | undefined): string | undefined {
  if (!f) return undefined;
  const val = f.atribs["val"] ?? f.atribs["start"];
  if (!val) return undefined;
  const partes = val.split("-");
  const base = ((): string => {
    if (partes.length === 1) return partes[0] as string;
    const [a, m, d] = partes.map((x) => Number(x));
    if (!a || !m) return val;
    const mes = MESES[m - 1] ?? "";
    return d ? `${d} ${mes} ${a}` : `${mes} ${a}`;
  })();
  if (f.etiqueta === "daterange" && f.atribs["stop"]) {
    return `BET ${base} AND ${f.atribs["stop"]}`;
  }
  const tipo = f.atribs["type"];
  if (tipo === "about") return `ABT ${base}`;
  if (tipo === "before") return `BEF ${base}`;
  if (tipo === "after") return `AFT ${base}`;
  if (f.atribs["quality"] === "estimated") return `EST ${base}`;
  if (f.atribs["quality"] === "calculated") return `CAL ${base}`;
  return base;
}

export interface ResultadoExportGedcom {
  texto: string;
  /** Personas cuyos dos apellidos se juntaron en un solo campo. */
  apellidosJuntados: number;
}

export function escribirGedcom(arbol: Arbol): ResultadoExportGedcom {
  const L: string[] = [];
  const w = (s: string): void => {
    L.push(s);
  };

  const xi = new Map<string, string>();
  let i = 0;
  for (const h of arbol.personas.keys()) xi.set(h, `@I${++i}@`);
  const xf = new Map<string, string>();
  let j = 0;
  for (const h of arbol.familias.keys()) xf.set(h, `@F${++j}@`);

  const hoy = new Date();
  w("0 HEAD");
  w("1 SOUR arbol-familiar-app");
  w("1 GEDC");
  w("2 VERS 5.5.1");
  w("2 FORM LINEAGE-LINKED");
  w("1 CHAR UTF-8");
  w(`1 DATE ${hoy.getDate()} ${MESES[hoy.getMonth()]} ${hoy.getFullYear()}`);

  const nombreLugar = (h: string | undefined): string | undefined =>
    h ? arbol.lugares.get(h)?.nombres[0]?.value : undefined;

  let juntados = 0;

  for (const [h, p] of arbol.personas) {
    const n = nombrePrincipal(p);
    const pila = n?.first?.trim() ?? "";
    const aps = (n?.apellidos ?? []).map((a) => a.texto.trim()).filter(Boolean);
    if (aps.length > 1) juntados++;
    const apellido = aps.join(" ");

    w(`0 ${xi.get(h)} INDI`);
    w(`1 NAME ${pila} /${apellido}/`);
    if (pila) w(`2 GIVN ${pila}`);
    if (apellido) w(`2 SURN ${apellido}`);
    w(`1 SEX ${p.sexo === "M" || p.sexo === "F" ? p.sexo : "U"}`);

    for (const tipo of ["Birth", "Death"] as const) {
      let ev: Evento | undefined;
      for (const r of p.eventos) {
        const e = arbol.eventos.get(r.hlink);
        if (e?.tipo === tipo) {
          ev = e;
          break;
        }
      }
      if (!ev) continue;
      const fecha = fechaAGedcom(ev.fecha);
      const lugar = nombreLugar(ev.lugar);
      if (!fecha && !lugar) continue;
      w(`1 ${tipo === "Birth" ? "BIRT" : "DEAT"}`);
      if (fecha) w(`2 DATE ${fecha}`);
      if (lugar) w(`2 PLAC ${lugar}`);
    }

    for (const fh of p.childof) if (xf.has(fh)) w(`1 FAMC ${xf.get(fh)}`);
    for (const fh of p.parentin) if (xf.has(fh)) w(`1 FAMS ${xf.get(fh)}`);
  }

  for (const [h, f] of arbol.familias) {
    w(`0 ${xf.get(h)} FAM`);
    if (f.padre && xi.has(f.padre)) w(`1 HUSB ${xi.get(f.padre)}`);
    if (f.madre && xi.has(f.madre)) w(`1 WIFE ${xi.get(f.madre)}`);
    for (const r of f.hijos) if (xi.has(r.hlink)) w(`1 CHIL ${xi.get(r.hlink)}`);
    if (f.rel === "Married") w("1 MARR");
  }

  w("0 TRLR");
  return { texto: L.join("\r\n") + "\r\n", apellidosJuntados: juntados };
}

// ---------------------------------------------------------------------------
// Importacion
// ---------------------------------------------------------------------------

interface Linea {
  nivel: number;
  etiqueta: string;
  valor: string;
  id?: string;
}

function trocear(texto: string): Linea[] {
  const out: Linea[] = [];
  for (const bruta of texto.split(/\r\n|\r|\n/)) {
    const linea = bruta.trim();
    if (!linea) continue;
    const m = /^(\d+)\s+(?:(@[^@]+@)\s+)?(\S+)(?:\s(.*))?$/.exec(linea);
    if (!m) continue;
    out.push({
      nivel: Number(m[1]),
      id: m[2],
      etiqueta: m[3] as string,
      valor: (m[4] ?? "").trim(),
    });
  }
  return out;
}

function fechaDeGedcom(bruto: string): Fecha | undefined {
  const t = bruto.trim().toUpperCase();
  if (!t) return undefined;

  const rango = /^BET(?:WEEN)?\s+(.+?)\s+AND\s+(.+)$/.exec(t);
  if (rango) {
    const a = unaFecha(rango[1] as string);
    const b = unaFecha(rango[2] as string);
    if (a && b) return { etiqueta: "daterange", atribs: { start: a, stop: b } };
  }

  const pref = /^(ABT|EST|CAL|BEF|AFT)\s+(.+)$/.exec(t);
  if (pref) {
    const v = unaFecha(pref[2] as string);
    if (!v) return { etiqueta: "datestr", atribs: { val: bruto.trim() } };
    const atribs: Record<string, string> = { val: v };
    const p = pref[1];
    if (p === "ABT") atribs["type"] = "about";
    else if (p === "BEF") atribs["type"] = "before";
    else if (p === "AFT") atribs["type"] = "after";
    else if (p === "EST") atribs["quality"] = "estimated";
    else if (p === "CAL") atribs["quality"] = "calculated";
    return { etiqueta: "dateval", atribs };
  }

  const v = unaFecha(t);
  if (v) return { etiqueta: "dateval", atribs: { val: v } };
  // Lo que no se entiende se guarda como texto en vez de tirarlo.
  return { etiqueta: "datestr", atribs: { val: bruto.trim() } };
}

function unaFecha(t: string): string | undefined {
  const soloAno = /^(\d{3,4})$/.exec(t);
  if (soloAno) return soloAno[1];
  const mesAno = /^([A-Z]{3})\s+(\d{3,4})$/.exec(t);
  if (mesAno) {
    const m = MESES.indexOf(mesAno[1] as string);
    if (m >= 0) return `${mesAno[2]}-${String(m + 1).padStart(2, "0")}`;
  }
  const completa = /^(\d{1,2})\s+([A-Z]{3})\s+(\d{3,4})$/.exec(t);
  if (completa) {
    const m = MESES.indexOf(completa[2] as string);
    if (m >= 0) {
      return `${completa[3]}-${String(m + 1).padStart(2, "0")}-${String(
        Number(completa[1]),
      ).padStart(2, "0")}`;
    }
  }
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  if (iso) return t;
  return undefined;
}

export interface ResultadoImportGedcom {
  arbol: Arbol;
  avisos: string[];
  /** Personas a las que se repartio el apellido en dos por suposicion. */
  apellidosDivididos: number;
}

export function leerGedcom(texto: string): ResultadoImportGedcom {
  const lineas = trocear(texto);
  const arbol = arbolVacio("1.7.2");
  const avisos: string[] = [];
  let divididos = 0;

  // id de GEDCOM -> handle propio
  const hPersona = new Map<string, string>();
  const hFamilia = new Map<string, string>();
  let np = 0;
  let nf = 0;
  for (const l of lineas) {
    if (l.nivel !== 0 || !l.id) continue;
    if (l.etiqueta === "INDI") hPersona.set(l.id, `_p${String(++np).padStart(3, "0")}`);
    else if (l.etiqueta === "FAM") hFamilia.set(l.id, `_f${String(++nf).padStart(3, "0")}`);
  }

  const lugares = new Map<string, string>();
  const lugarDe = (nombre: string): string => {
    const clave = nombre.trim().toLowerCase();
    const ya = lugares.get(clave);
    if (ya) return ya;
    const handle = `_l${String(lugares.size + 1).padStart(3, "0")}`;
    const l: Lugar = {
      handle,
      id: `P${String(lugares.size + 1).padStart(4, "0")}`,
      change: "0",
      tipo: "Unknown",
      nombres: [{ value: nombre.trim(), otrosAtribs: {} }],
      otrosAtribs: {},
      otros: {},
    };
    arbol.lugares.set(handle, l);
    lugares.set(clave, handle);
    return handle;
  };

  let ne = 0;
  const nuevoEvento = (tipo: string, fecha?: Fecha, lugar?: string): string => {
    const handle = `_e${String(++ne).padStart(3, "0")}`;
    const e: Evento = {
      handle,
      id: `E${String(ne).padStart(4, "0")}`,
      change: "0",
      tipo,
      fecha,
      lugar,
      otrosAtribs: {},
      otros: {},
    };
    arbol.eventos.set(handle, e);
    return handle;
  };

  let i = 0;
  while (i < lineas.length) {
    const cab = lineas[i] as Linea;
    if (cab.nivel !== 0 || !cab.id) {
      i++;
      continue;
    }
    // Todas las lineas del registro.
    let j = i + 1;
    while (j < lineas.length && (lineas[j] as Linea).nivel > 0) j++;
    const cuerpo = lineas.slice(i + 1, j);

    if (cab.etiqueta === "INDI") {
      const handle = hPersona.get(cab.id) as string;
      const p: Persona = {
        handle,
        // El identificador visible se asigna al final, renumerando en orden.
        id: undefined,
        change: "0",
        sexo: "U",
        nombres: [],
        eventos: [],
        childof: [],
        parentin: [],
        otrosAtribs: {},
        otros: {},
      };

      let pila = "";
      let apellidos = "";
      for (let k = 0; k < cuerpo.length; k++) {
        const l = cuerpo[k] as Linea;
        if (l.nivel === 1 && l.etiqueta === "NAME") {
          const m = /^(.*?)\s*\/(.*?)\/\s*(.*)$/.exec(l.valor);
          if (m) {
            pila = (m[1] ?? "").trim();
            apellidos = (m[2] ?? "").trim();
          } else {
            pila = l.valor.trim();
          }
        } else if (l.nivel === 2 && l.etiqueta === "GIVN") pila = l.valor || pila;
        else if (l.nivel === 2 && l.etiqueta === "SURN") apellidos = l.valor || apellidos;
        else if (l.nivel === 1 && l.etiqueta === "SEX") {
          const v = l.valor.trim().toUpperCase();
          p.sexo = (v === "M" || v === "F" ? v : "U") as Sexo;
        } else if (l.nivel === 1 && (l.etiqueta === "BIRT" || l.etiqueta === "DEAT")) {
          let fecha: Fecha | undefined;
          let lugar: string | undefined;
          for (let z = k + 1; z < cuerpo.length && (cuerpo[z] as Linea).nivel > 1; z++) {
            const s = cuerpo[z] as Linea;
            if (s.etiqueta === "DATE") fecha = fechaDeGedcom(s.valor);
            else if (s.etiqueta === "PLAC" && s.valor.trim()) lugar = lugarDe(s.valor);
          }
          if (fecha || lugar) {
            const eh = nuevoEvento(l.etiqueta === "BIRT" ? "Birth" : "Death", fecha, lugar);
            p.eventos.push({ hlink: eh, role: "Primary", otrosAtribs: {}, otros: {} });
          }
        } else if (l.nivel === 1 && l.etiqueta === "FAMC") {
          const f = hFamilia.get(l.valor.trim());
          if (f) p.childof.push(f);
        } else if (l.nivel === 1 && l.etiqueta === "FAMS") {
          const f = hFamilia.get(l.valor.trim());
          if (f) p.parentin.push(f);
        }
      }

      // Reparto de los apellidos. GEDCOM los trae juntos; si son exactamente
      // dos palabras se aplica la convencion espanola. Es una suposicion.
      const trozos = apellidos.split(/\s+/).filter(Boolean);
      const lista = [];
      if (trozos.length === 2) {
        divididos++;
        lista.push({
          texto: trozos[0] as string,
          prim: true,
          derivation: "Patrilineal",
          otrosAtribs: {},
        });
        lista.push({
          texto: trozos[1] as string,
          prim: false,
          derivation: "Matrilineal",
          otrosAtribs: {},
        });
      } else if (trozos.length) {
        // Una sola palabra, o tres o mas (apellidos compuestos): se deja en
        // un solo campo sin suponer el origen, que es mas honesto que
        // partirlo mal.
        lista.push({
          texto: trozos.join(" "),
          prim: true,
          otrosAtribs: {},
        });
      }

      p.nombres.push({
        atribs: { type: "Birth" },
        first: pila || undefined,
        apellidos: lista,
        otros: {},
      });
      arbol.personas.set(handle, p);
    } else if (cab.etiqueta === "FAM") {
      const handle = hFamilia.get(cab.id) as string;
      const f: Familia = {
        handle,
        id: undefined,
        change: "0",
        rel: "Unknown",
        eventos: [],
        hijos: [],
        otrosAtribs: {},
        otros: {},
      };
      for (const l of cuerpo) {
        if (l.nivel !== 1) continue;
        if (l.etiqueta === "HUSB") f.padre = hPersona.get(l.valor.trim());
        else if (l.etiqueta === "WIFE") f.madre = hPersona.get(l.valor.trim());
        else if (l.etiqueta === "CHIL") {
          const c = hPersona.get(l.valor.trim());
          if (c) f.hijos.push({ hlink: c, otrosAtribs: {}, otros: {} });
        } else if (l.etiqueta === "MARR") f.rel = "Married";
      }
      arbol.familias.set(handle, f);
    }

    i = j;
  }

  // Los identificadores visibles se renumeran en orden, que es mas legible
  // que arrastrar los del fichero de origen.
  let k = 0;
  for (const p of arbol.personas.values()) p.id = `I${String(++k).padStart(4, "0")}`;
  k = 0;
  for (const f of arbol.familias.values()) f.id = `F${String(++k).padStart(4, "0")}`;

  // Coherencia de las tres relaciones: GEDCOM solo trae dos (FAMC/FAMS en la
  // persona y CHIL/HUSB/WIFE en la familia), asi que se cuadran aqui.
  for (const [fh, f] of arbol.familias) {
    for (const r of f.hijos) {
      const p = arbol.personas.get(r.hlink);
      if (p && !p.childof.includes(fh)) p.childof.push(fh);
    }
    for (const x of [f.padre, f.madre]) {
      if (!x) continue;
      const p = arbol.personas.get(x);
      if (p && !p.parentin.includes(fh)) p.parentin.push(fh);
    }
  }
  for (const [ph, p] of arbol.personas) {
    p.childof = p.childof.filter((fh) => {
      const f = arbol.familias.get(fh);
      if (!f) return false;
      if (!f.hijos.some((r) => r.hlink === ph)) {
        f.hijos.push({ hlink: ph, otrosAtribs: {}, otros: {} });
      }
      return true;
    });
    p.parentin = p.parentin.filter((fh) => {
      const f = arbol.familias.get(fh);
      if (!f) return false;
      if (f.padre !== ph && f.madre !== ph) {
        // FAMS sin HUSB/WIFE que lo respalde: se descarta la referencia en
        // vez de inventar un rol.
        return false;
      }
      return true;
    });
  }

  if (divididos) {
    avisos.push(
      `se repartieron en dos los apellidos de ${divididos} persona(s) ` +
        "suponiendo la convención española (primero del padre, segundo de la " +
        "madre). GEDCOM no guarda ese dato, así que conviene revisarlo.",
    );
  }
  avisos.push(
    "GEDCOM no distingue el origen de cada apellido ni los tipos de unión " +
      "distintos de «casados». Para no perder nada, usa .gramps.",
  );

  return { arbol, avisos, apellidosDivididos: divididos };
}
